/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { sanitizeHtml } from "../../../utils/sanitize";
import Checkbox from "../../ui/form/Checkbox";
import { Modal, ModalBody, ModalClose, ModalContent, ModalFooter, ModalHeader, ModalTitle, ModalTrigger } from "../../ui/Modal";
import Anchor from "../../ui/Anchor";
import LoadingIndicator from "../../LoadingIndicator";
import ButtonGroup from "../../ui/ButtonGroup";
import Button from "../../ui/Button";
import Text from "../../ui/Text";

/**
 * Single parameterized terms/policy checkbox: a required Checkbox whose label is a
 * link that opens a modal with the lazily-fetched terms text + an "I agree" action.
 *
 * Variant inputs (supplied by the consumer):
 * - queryHook: a react-query hook returning `{ refetch, data, isLoading }`, called with
 *   `{ enabled: false }` so the text is fetched only when the modal first opens.
 * - label: anchor content (the clickable link text).
 * - title: modal title content.
 */
const TermsCheckbox = ({ name, control, required, onChange, onAgree, queryHook, label, title }) => {
  const { refetch, data, isLoading, isError } = queryHook({ enabled: false });

  const handleModalOpen = () => {
    if (!data) {
      refetch();
    }
  };

  return (
    <Checkbox control={control} name={name} required={required} disableLabelClick={true} onChange={onChange}>
      <Modal onOpen={handleModalOpen}>
        <ModalTrigger>
          <Anchor underline="always">{label}</Anchor>
        </ModalTrigger>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {isLoading ? (
              <LoadingIndicator page />
            ) : isError ? (
              <Text color="error" role="alert">Something went wrong</Text>
            ) : (
              <Text color="dark" dangerouslySetInnerHTML={{ __html: sanitizeHtml(data) }} />
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup itemsWidth="half">
              <ModalClose>
                <Button variant="outline" title="Close" />
              </ModalClose>
              <ModalClose>
                <Button variant="primary" title="i_agree" onClick={onAgree} />
              </ModalClose>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Checkbox>
  );
};

export default TermsCheckbox;
